import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { Button, Card, ErrorNote, Field, inputClass, Loading, Logo } from '../components/ui';
import { authRequest, signInWith } from '../lib/api';
import { useMe, useSignInMethods } from '../lib/queries';

export function AuthShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2">
          <Logo className="h-9 w-9" />
          <span className="font-display text-2xl font-semibold tracking-tight">Vardex</span>
        </div>
        <Card className="p-6">
          <h1 className="mb-4 text-xl font-semibold">{title}</h1>
          {children}
        </Card>
      </div>
    </div>
  );
}

export function ProviderButtons({ next }: { next: string }) {
  const methods = useSignInMethods();
  const [error, setError] = useState<unknown>(null);
  if (methods.isPending) return <Loading />;
  const m = methods.data;
  const go = (provider: 'microsoft' | 'google') => signInWith(provider, next).catch(setError);
  return (
    <div className="space-y-2">
      {m?.microsoft && <Button variant="primary" className="w-full py-2" onClick={() => go('microsoft')}>Sign in with Microsoft</Button>}
      {m?.google && <Button className="w-full py-2" onClick={() => go('google')}>Sign in with Google</Button>}
      {m && !m.microsoft && !m.google && !m.password && <p className="text-sm text-muted">No sign-in method is set up on this server.</p>}
      <ErrorNote error={error} />
    </div>
  );
}

export function SignIn() {
  const me = useMe();
  const [params] = useSearchParams();
  const next = params.get('next') || '/';
  const methods = useSignInMethods();

  if (me.data) return <Navigate to={next} replace />;
  return (
    <AuthShell title="Sign in">
      {params.get('error') && <div className="mb-4"><ErrorNote error={new Error('Sign-in did not work. If you have not been invited yet, ask your admin for an invitation.')} /></div>}
      <ProviderButtons next={next} />
      {methods.data?.password && <PasswordForm next={next} />}
      <p className="mt-5 text-xs text-muted">Access is by invitation. Use the Microsoft or Google account with the email address you were invited with.</p>
    </AuthShell>
  );
}

/** Local development only: seeded test users. */
function PasswordForm({ next }: { next: string }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await authRequest('/sign-in/email', { email, password });
      await queryClient.invalidateQueries();
      navigate(next, { replace: true });
    } catch (err) {
      setError(err);
    }
  };
  return (
    <form onSubmit={submit} className="mt-5 border-t border-rule pt-4">
      <div className="mb-3 text-xs font-medium uppercase tracking-wide text-muted">Local development</div>
      <Field label="Email"><input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required /></Field>
      <Field label="Password"><input className={inputClass} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required /></Field>
      <ErrorNote error={error} />
      <Button type="submit" className="mt-2 w-full">Sign in with password</Button>
    </form>
  );
}
